import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from "@angular/core";
import { IEndpoint, StreamTypes } from "@auvious/rtc";
import { merge, Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";

import { fadeInOut } from "../../core-ui.animations";
import {
  ConversationTypeEnum,
  EndpointTypeEnum,
  LayoutEnum,
  TileTypeEnum,
  UserRoleEnum,
  VideoFacingModeEnum,
} from "../../core-ui.enums";
import {
  CustomerParam,
  IArea,
  IEndpointMetadata,
  IStreamMetadataChangedEvent,
  ITile,
  PublicParam,
} from "../../models";
import { IInteraction } from "../../models/IInteraction";
import {
  AppConfigService,
  ArPointerActivatedEvent,
  ArPointerDeactivatedEvent,
  ConferenceService,
  ConferenceStore,
  debugError,
  DeviceService,
  EndpointState,
  LocalMediaService,
  MediaEffectsService,
  MediaRulesService,
  PointerService,
  StreamState,
  UserService,
} from "../../services";
import { NotificationService } from "../../services/notification.service";
import { BandwidthAdaptationService } from "../../services/bandwidth.adaptation.service";
import { TranslateService } from "@ngx-translate/core";

const SKETCH_ACTIONS_COLUMN_LAYOUT_BREAKPOINT = 540;
const TINY_VIEW_LAYOUT_BREAKPOINT = 180;

@Component({
  selector: "app-tile",
  templateUrl: "./tile.component.html",
  styleUrls: ["./tile.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut],
})
export class TileComponent implements OnInit, AfterViewInit, OnDestroy, ITile {
  private _stream: StreamState;

  @Input() set stream(s: StreamState) {
    this._stream = s;

    if (this.userState?.id !== s.originator.endpoint) {
      this.userState = this.store.getEndpoint(s.originator.endpoint);
    }
  }

  public get stream(): StreamState {
    return this._stream;
  }

  @Input() interaction: IInteraction;

  @Input() isSpotlight: boolean;

  @Output() ready = new EventEmitter<TileComponent>();
  @Output() destroyed = new EventEmitter<TileComponent>();
  @Output() viewSizeChanged = new EventEmitter<IArea>();

  private userState: EndpointState;
  streamVolume: number;
  ArPointerOn = false;

  isFreezeFrameNotFromMute = false;
  isZoomOn = false;
  isStreamElementReady = false;
  isFilterLoading = false;
  isTinyView = signal(false);
  isAvatarError = signal(false);
  isSketchControlActive = false;
  isPointerActive = signal(false);
  isPopedOut = false;
  isSketchActionsCondensed = false;
  isWhisper = false;

  hostSize = signal<IArea>({ width: 0, height: 0 });
  sketchId = signal<string>(undefined);
  tileLayoutType = signal<LayoutEnum>(undefined);
  roomLayoutType = signal<LayoutEnum>(undefined);
  participant = signal<IEndpoint<IEndpointMetadata>>(undefined);
  shortName = signal<string>(undefined);

  isAIAgent = computed(
    () => this.participant()?.metadata?.type === EndpointTypeEnum.aiAssistant
  );

  protected element: HTMLVideoElement;
  private subscriptions: Subscription;
  private _ratio = 1;

  constructor(
    private host: ElementRef<HTMLDivElement>,
    private conferenceService: ConferenceService,
    private deviceService: DeviceService,
    private cd: ChangeDetectorRef,
    private mediaFilterService: MediaEffectsService,
    private notification: NotificationService,
    private zone: NgZone,
    private userService: UserService,
    private pointerService: PointerService,
    private mediaRules: MediaRulesService,
    private mediaService: LocalMediaService,
    private config: AppConfigService,
    public store: ConferenceStore,
    public bandwidthAdaptation: BandwidthAdaptationService,
    public translation: TranslateService
  ) {
    this.subscriptions = new Subscription();
  }

  ngOnInit(): void {
    // this.subscriptions.add(
    //   this.conferenceService.streamMutedChange$
    //     .pipe(filter((data) => data.stream.id === this.stream.id))
    //     .subscribe((_) => {
    //       this.cd.detectChanges();
    //     })
    // );

    this.isWhisper = !!this.interaction?.getWhisperMode();

    this.host.nativeElement.setAttribute("role", "group");
    this.setAriaLabel();

    this.subscriptions.add(
      merge(
        this.conferenceService.streamState$,
        this.conferenceService.endpointState$
      ).subscribe((_) => {
        this.setAriaLabel();
        this.cd.detectChanges();
      })
    );

    this.subscriptions.add(
      this.store.updated$
        .pipe(
          filter(
            () =>
              !!this.store.layout &&
              this.roomLayoutType() !== this.store.layout?.type
          )
        )
        .subscribe(() => {
          this.roomLayoutType.set(this.store.layout.type);
        })
    );
    this.subscriptions.add(
      this.conferenceService.participantsChanged$
        .pipe(
          filter(
            (s) => !!this.conferenceService.getParticipants()[this.endpoint]
          )
        )
        .subscribe((participants) => {
          const p = participants[this.endpoint];
          this.participant.set(p);
          try {
            const name = this.participant()?.metadata?.name?.trim();
            if (!name) return;

            this.shortName.set(
              name
                .split(" ")
                .reduce(
                  (short, fragment) =>
                    (short +=
                      fragment?.length > 0 ? fragment[0].toUpperCase() : ""),
                  ""
                )
            );
          } catch (ex) {
            debugError(ex);
          }
        })
    );

    this.subscriptions.add(
      this.mediaFilterService.filterLoading$
        .pipe(filter((_) => this.isLocal))
        .subscribe((_) => {
          this.isFilterLoading = true;
          this.cd.detectChanges();
        })
    );

    this.subscriptions.add(
      merge(
        this.mediaFilterService.filterLoaded$,
        this.mediaFilterService.filterDisabled$
      )
        .pipe(filter(() => this.isLocal))
        .subscribe(() => {
          this.isFilterLoading = false;
          this.cd.detectChanges();
        })
    );
    this.subscriptions.add(
      // take(1), not to spam ui with errors
      this.mediaFilterService.filterError$
        .pipe(
          filter(() => this.isLocal),
          take(1)
        )
        .subscribe((error) => {
          this.isFilterLoading = false;
          let body = "Could not apply effect to video.";

          switch (error) {
            case "ImageUnusable":
              body = "Could not load image";
              break;
            case "PublishFailed":
            case "UnpublishFailed":
              body = "Could not notify participants of the effect change";
              break;
            case "VideoElementNotFound":
            case "VideoTrackNotFound":
              body = "Could not find video to apply the effect";
              break;
          }

          this.notification.error("Effect failed", { body });
          this.cd.detectChanges();
        })
    );
    // this.subscriptions.add(
    //   // only for local video
    //   this.conferenceService.streamMuteWillChange$
    //     .pipe(filter((s) => s.stream.id === this.stream.id))
    //     .subscribe((data) => {
    //       this.muteChange(data.mute, data.trackKind);
    //     })
    // );
    // this.subscriptions.add(
    //   // for remote
    //   this.conferenceService.streamMutedChange$
    //     .pipe(filter((s) => s.stream.id === this.stream.id && !this.isLocal))
    //     .subscribe((data) => {
    //       this.muteChange(data.muted, data.trackKind);
    //     })
    // );
    this.subscriptions.add(
      this.pointerService.freezeFrameChange$
        .pipe(
          filter(
            (e) =>
              e.target.endpoint === this._stream.originator.endpoint &&
              e.correlationId === this._stream.correlationId
          )
        )
        .subscribe((e) => {
          // this.isFreezeFrameBlurred = false;
          // this.isFreezeFrameVisible = e.on;
          this.isFreezeFrameNotFromMute = e.on;
          this.cd.detectChanges();
        })
    );
    this.subscriptions.add(
      this.pointerService.zoomFrameChange$
        .pipe(
          filter(
            (e) =>
              e.target.endpoint === this._stream.originator.endpoint &&
              e.correlationId === this._stream.correlationId
          )
        )
        .subscribe((e) => {
          this.isZoomOn = e.on;
          this.cd.detectChanges();
        })
    );

    this.subscriptions.add(
      this.pointerService.pointAreaChange$
        .pipe(
          filter(
            (event) =>
              event.targetUserEndpointId === this.endpoint &&
              event.targetStreamCorrelationId === this._stream.correlationId
            // event.targetStreamType === this._stream.type
          )
        )
        .subscribe(async (event) => {
          switch (event.type) {
            case ArPointerActivatedEvent.type:
              this.isPointerActive.set(true);
              break;
            case ArPointerDeactivatedEvent.type:
              this.isPointerActive.set(false);
              break;
          }
          // wait for animation to end
          setTimeout((_) => this.cd.detectChanges(), 200);
        })
    );
    this.subscriptions.add(
      this.conferenceService.streamMetadataChanged$
        .pipe(filter((e) => e.streamId === this._stream.id))
        .subscribe((e: IStreamMetadataChangedEvent) => {
          this.ready.emit(this);
        })
    );

    // caused more trouble than what it solved
    this.subscriptions.add(
      this.mediaRules.pictureInPictureChanged$
        .pipe(
          filter((e) => e.streamId === this._stream.id)
          // delay(200)
        )
        .subscribe((e) => {
          this.isPopedOut = e.on;
          this.cd.detectChanges();
        })
    );

    this.isPointerActive.set(
      this.pointerService.isTarget(
        this.endpoint,
        this.stream.type,
        this.stream.correlationId
      )
    );
  }

  ngAfterViewInit() {
    try {
      // if the tile is too small, the volume indicator oveflows. Listen for resizes and change class accordingly
      this.zone.runOutsideAngular(() => {
        const ro = new ResizeObserver((entries) => {
          const entry = entries?.pop();
          const size = entry?.contentRect.height <= TINY_VIEW_LAYOUT_BREAKPOINT;
          if (size !== this.isTinyView()) {
            this.isTinyView.set(size);
          }

          this.isSketchActionsCondensed =
            entry?.contentRect.width < SKETCH_ACTIONS_COLUMN_LAYOUT_BREAKPOINT;

          this.hostSize.set({
            width: entry?.contentRect.width,
            height: entry?.contentRect.height,
          });
          this.viewSizeChanged.emit(this.hostSize());
          // debug('tile resized notified', this.stream.id, this.hostSize);
          this.cd.markForCheck();
        });
        ro.observe(this.host.nativeElement);
      });
    } catch (ex) {
      debugError(ex);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.destroyed.emit(this);
  }

  @HostBinding("class") get class() {
    // debug('tile class generated', this.stream.id, this.isTinyView);
    return {
      "tile-local": this.isLocal,
      "tile-facing-user": this.isLocalMirrored,
      "tile-screen": this.isScreen && !this.isPointerActive(),
      "tile-cam-off": this.isMutedVideo,
      "tile-float": this.isLayoutFloating(),
      "tile-small": this.isTinyView(),
      "tile-portrait": !this.isScreen,
      "tile-pointer-on": this.isPointerActive(),
      "tile-spotlight": this.isSpotlight,
      // 'tile-pip': this.isPopedOut,
      // [`tile-color-${this.color}`]: !!this.color
    };
  }

  private get isLocalMirrored() {
    return (
      ((this.userService.isCustomer &&
        this.config.customerParamEnabled(
          CustomerParam.MIRROR_USER_CAM_ENABLED
        )) ||
        !this.userService.isCustomer) &&
      this.isLocal &&
      this.isFacingModeUser
    );
  }

  // muteChange(mute: boolean, trackKind: StreamTrackKindEnum) {
  //   if (this.isFreezeFrameNotFromMute) {
  //     return;
  //   }

  //   if (mute) {
  //     this.isFreezeFrameVisible =
  //       trackKind === StreamTrackKindEnum.video || this.isMutedVideo;
  //   } else {
  //     setTimeout(() => (this.isFreezeFrameBlurred = false), 200);
  //   }

  //   this.cd.detectChanges();
  // }

  private setAriaLabel() {
    if (this.participant()) {
      const isAgent = this.participant().metadata?.roles?.includes(
        UserRoleEnum.agent
      );

      this.host.nativeElement.setAttribute(
        "aria-label",
        this.translation.instant(
          isAgent || this.isAIAgent() ? "Agent" : "Customer"
        )
      );
    } else {
      this.host.nativeElement.setAttribute(
        "aria-label",
        this.translation.instant("Participant")
      );
    }
  }

  public get isFreezeFrameVisible() {
    return (
      this.isMutedVideo || this._stream.video.freeze || this.isViewerDisabled
    );
  }

  public get isFreezeFrameBlurred() {
    return this.isMutedVideo && !this._stream.video.freeze;
  }

  public get isViewerDisabled() {
    return this._stream.video.disabledFromBitrateAdaptation;
  }

  /* stream element events */

  streamElementReady(element: HTMLVideoElement) {
    this.conferenceService.bindElement(
      (this.element = element),
      this.stream.id
    );

    // TODO: some audio tracks also no canplay, what do
    // for muted streams, 'canplay' event never fires, so we need to inform manually
    if (this.isMutedVideo) {
      this._ratio = 1;
      // debug('tile:streamElementReady');
      this.ready.emit(this);
      this.isStreamElementReady = true;
      this.cd.detectChanges();
    }

    element.addEventListener("canplay", (e) => {
      if (!this._ratio) {
        this._ratio = this.element?.videoWidth / this.element?.videoHeight || 1;
      }
      // debug('tile:canPlay');
      this.ready.emit(this);
      this.isStreamElementReady = true;
      this.cd.detectChanges();
    });

    element.addEventListener("resize", (e) => {
      if (
        !this.isStreamElementReady ||
        this.element?.videoWidth === 0 ||
        this.element?.videoHeight === 0
      ) {
        return;
      }

      const ratio = this.element?.videoWidth / this.element?.videoHeight || 1;
      if (ratio !== this._ratio) {
        this._ratio = ratio;
        // debug('tile:resize');
        this.ready.emit(this);
      }
    });
  }

  streamElementDestroyed(event: {
    element: HTMLVideoElement;
    stream: MediaStream;
    id: string;
  }) {
    this.conferenceService.unbindElement(event.element, event.id);
  }

  autoplayBlocked(id: string) {
    this.conferenceService.requestExplicitPlay();
  }

  stopScreen() {
    this.mediaService.closeScreenStream();
  }

  avatarError() {
    this.isAvatarError.set(true);
  }

  sketchControlChanged(active: boolean) {
    this.isSketchControlActive = active;
  }

  sketchConnected(id: string) {
    this.sketchId.set(id);
  }

  sketchEnded(id: string) {
    this.sketchId.set(null);
    this.isSketchControlActive = false;
  }

  pointControlSelected() {
    this.isSketchControlActive = false;
  }

  setLayoutType(type: LayoutEnum) {
    this.tileLayoutType.set(type);
  }

  /* view getters */

  isHiddenTooSmall = computed(
    () =>
      (this.hostSize().height < 180 || this.hostSize().width < 180) &&
      this.pointerService.isStarted &&
      !this.pointerService.isTarget(
        this._stream.originator.endpoint,
        this._stream.type,
        this._stream.correlationId
      )
  );

  isSketchAreaActive = computed(
    () => !!this.sketchId() && this.isPointerActive()
  );

  get isPortraitMode() {
    return this._stream.video.portraitMode;
  }

  get isOrientationPortrait() {
    // video is forced to be be in portrait by settings
    return (
      this.isPortraitMode ||
      // video is originally in portrait (mobile phone)
      this.element?.videoWidth < this.element?.videoHeight
    );
  }

  avatar = computed(() => {
    if (this.isAvatarError()) {
      return;
    }
    const isStreamAgent =
      this.stream.originator.username ===
        this.conferenceService.myself.username && this.userService.isAgent;
    if (
      (this.isParticipantAgent() || isStreamAgent) &&
      !!this.config.publicParam(PublicParam.AGENT_AVATAR_MASK)
    ) {
      return this.config.publicParam(PublicParam.AGENT_AVATAR_MASK);
    }
    if (
      this.conferenceService.myself.endpoint ===
      this._stream?.originator?.endpoint
    ) {
      return this.userService.getUserDetails()?.avatarUrl;
    }
    return this.participant()?.metadata?.avatarUrl;
  });

  displayName = computed(() => {
    if (
      this.isParticipantAgent() &&
      !!(
        this.config.publicParam(PublicParam.AGENT_NAME_MASK) as string
      )?.trim() &&
      this.interaction.getType() !== ConversationTypeEnum.voiceCall
    ) {
      return this.config.publicParam(PublicParam.AGENT_NAME_MASK).trim();
    }
    const name = this.participant()?.metadata?.name?.trim();

    return this.isTinyView() &&
      this.isRoomLayoutSpotlight() &&
      !!this.shortName()
      ? this.shortName()
      : name;
  });

  isDisplayNameAvailable = computed(
    () =>
      !!this.displayName() &&
      (!this.isParticipantAgent() ||
        (this.isParticipantAgent() &&
          this.config.publicParam(PublicParam.AGENT_NAME_ENABLED)))
  );

  get uuid() {
    return this._stream.id;
  }

  get type() {
    return this.isScreen
      ? TileTypeEnum.screenShare
      : this.ArPointerOn
      ? TileTypeEnum.arPointer
      : TileTypeEnum.video;
  }

  get isStreamPublished() {
    return "replace" in this._stream;
  }

  get ratio() {
    return this.isPortraitMode
      ? 9 / 14
      : this.element &&
        this.element.videoHeight > 0 &&
        this.element.videoWidth > 0
      ? this.element.videoWidth / this.element.videoHeight
      : this._ratio;
  }

  get isScreen() {
    return this._stream.type === StreamTypes.SCREEN;
  }

  get isVideo() {
    return (
      this._stream.type === StreamTypes.VIDEO ||
      this._stream.type === StreamTypes.CAM
    );
  }

  get container() {
    return this.host.nativeElement;
  }

  isLayoutFloating = computed(
    () => this.tileLayoutType() === LayoutEnum.floating
  );

  isRoomLayoutSpotlight = computed(
    () => this.roomLayoutType() === LayoutEnum.spotlight
  );

  get isFacingModeUser() {
    return this._stream.video.facingMode === VideoFacingModeEnum.User;
  }

  get isDeviceSetup() {
    return this.deviceService.isDevicesSetup;
  }

  get isLocal() {
    return (
      this._stream.originator.endpoint === this.store.endpoint &&
      this._stream.primary
    );
  }

  get isMirror() {
    return this.isLocal && this.isFacingModeUser;
  }

  get isMutedAudio() {
    return (
      this._stream.audio.state === "muted" ||
      this._stream.audio.state === "disabled"
    );
  }

  get isMutedVideo() {
    return (
      this._stream.video.state === "muting" ||
      this._stream.video.state === "muted" ||
      this._stream.video.state === "disabled"
    );
  }

  get endpoint() {
    return this._stream.originator.endpoint;
  }

  isParticipantAgent = computed(() =>
    this.participant()?.metadata?.roles?.includes(UserRoleEnum.agent)
  );

  get isSketchActive() {
    return this.pointerService.isTarget(
      this.endpoint,
      this._stream.type,
      this._stream.correlationId
    );
  }

  get isMirroringHelpActive() {
    return this.isScreen && this.streamDisplaySurface !== "window";
  }

  isScreenHelpActive = computed(
    () => this.isScreen && this.isLocal && !this.isPointerActive()
  );

  get streamDisplaySurface() {
    return this.stream.video.display;
  }
}
